/**
 * Reusable tab step navigation component with validation.
 * Supports multi-step forms with Bootstrap tabs, form validation, and configurable behavior.
 */

import Tab from 'bootstrap/js/dist/tab'

/** Containers that have already been initialized to prevent duplicate listeners */
const initializedContainers = new WeakSet<HTMLElement | Document>()

/**
 * Configuration options for tab step initialization
 */
export interface TabStepConfig {
  /** Container selector or element (default: document) */
  container?: string | HTMLElement | Document

  /** Initial tab hash to show (default: first tab found) */
  defaultHash?: string

  /** Enable URL hash updates (default: true) */
  updateHash?: boolean

  /** Enable scroll to top on tab change (default: true) */
  scrollToTop?: boolean

  /** Auto-focus selectors for specific tabs { '#tab-hash': 'input#selector' } */
  autoFocus?: { [key: string]: string }

  /** Callback when tab changes */
  onTabChange?: (hash: string) => void
}

/**
 * Validate inputs within a tab and return invalid inputs
 */
export function validateTabStepInputs(
  tab: HTMLAnchorElement,
  container: HTMLElement | Document = document,
): HTMLInputElement[] {
  const tabContent = container.querySelector(tab.hash) as HTMLElement
  if (!tabContent) return []

  const tabInputs =
    tabContent.querySelectorAll<HTMLInputElement>('input, select')
  const invalidInputs: HTMLInputElement[] = []
  for (const input of tabInputs) {
    if (!input.checkValidity()) {
      invalidInputs.push(input)
    }
  }
  return invalidInputs
}

/**
 * Validate tab and update visual state (valid/invalid classes)
 */
export function validateTabStepChange(
  tab: HTMLAnchorElement,
  container: HTMLElement | Document = document,
): HTMLInputElement[] {
  const invalidInputs = validateTabStepInputs(tab, container)

  tab.classList.toggle('invalid', invalidInputs.length > 0)
  tab.classList.toggle('valid', invalidInputs.length === 0)

  return invalidInputs
}

/**
 * Show a specific tab by hash
 */
function showTabStep(
  hash: string,
  force: boolean,
  config: Required<TabStepConfig>,
): void {
  const container =
    typeof config.container === 'string'
      ? (document.querySelector(config.container) as HTMLElement)
      : (config.container as HTMLElement | Document)

  if (!container) return

  const currentTab = container.querySelector<HTMLAnchorElement>(
    '.nav .nav-link.active',
  )
  const targetTab = container.querySelector(
    `a[href="${hash}"]`,
  ) as HTMLAnchorElement

  if (!targetTab) return

  // Validate current tab before switching (unless forced)
  if (currentTab && !force) {
    const invalidInputs = validateTabStepChange(
      currentTab,
      container as HTMLElement | Document,
    )
    if (invalidInputs.length > 0) {
      invalidInputs[0].reportValidity()
      return
    }
  }

  // Switch to target tab
  const tabTrigger = new Tab(targetTab)
  tabTrigger.show()

  // Restore focus to container to maintain keyboard accessibility (e.g. ESC in modals).
  // Tab transitions hide the old pane (display:none), which moves focus to document.body.
  if (container instanceof HTMLElement && container.hasAttribute('tabindex')) {
    container.focus()
  }

  // Update URL hash if enabled
  if (config.updateHash) {
    window.location.hash = targetTab.hash
  }

  // Scroll to top if enabled
  if (config.scrollToTop) {
    window.scrollTo(0, 0)
  }

  // Handle auto-focus for this tab
  if (config.autoFocus && config.autoFocus[hash]) {
    setTimeout(() => {
      const focusTarget = container.querySelector(
        config.autoFocus[hash],
      ) as HTMLInputElement
      if (focusTarget) {
        focusTarget.focus()
      }
    }, 100)
  }

  // Call onChange callback
  if (config.onTabChange) {
    config.onTabChange(hash)
  }

  // Re-validate if returning to previously invalid tab
  if (targetTab.classList.contains('invalid')) {
    const invalidInputs = validateTabStepInputs(targetTab, container)
    if (invalidInputs.length > 0) {
      setTimeout(() => invalidInputs[0].reportValidity(), 200)
    }
  }
}

/**
 * Initialize tab step navigation with optional configuration.
 * Returns a destroy function to remove all listeners, or undefined if already initialized.
 */
export function initTabSteps(
  userConfig?: TabStepConfig,
): (() => void) | undefined {
  // Merge user config with defaults
  const config: Required<TabStepConfig> = {
    container: document as Document | HTMLElement,
    defaultHash: '#',
    updateHash: true,
    scrollToTop: true,
    autoFocus: {},
    onTabChange: () => {
      // No-op callback by default
    },
    ...userConfig,
  }

  const container =
    typeof config.container === 'string'
      ? (document.querySelector(config.container) as HTMLElement)
      : (config.container as HTMLElement | Document)

  if (!container) {
    console.warn(`[initTabSteps] Container not found: ${config.container}`)
    return undefined
  }

  if (initializedContainers.has(container)) {
    console.warn('[initTabSteps] Container already initialized, skipping')
    return undefined
  }
  initializedContainers.add(container)

  const controller = new AbortController()
  const { signal } = controller

  // Initialize navigation buttons (.tab-next, .tab-previous)
  const buttons = container.querySelectorAll<HTMLAnchorElement>(
    '.tab-next, .tab-previous',
  )
  for (const button of buttons) {
    button.addEventListener(
      'click',
      function (event) {
        event.preventDefault()
        // Skip validation when going back
        const force = button.classList.contains('tab-previous')
        showTabStep(button.hash, force, config)
      },
      { signal },
    )
  }

  // Initialize tab step indicators (.nav .nav-link)
  const tabs = container.querySelectorAll<HTMLAnchorElement>('.nav .nav-link')
  for (const tab of tabs) {
    tab.addEventListener(
      'click',
      function (event) {
        event.preventDefault()
        if (tab.hash) {
          // Allow direct navigation to tabs (force = true)
          showTabStep(tab.hash, true, config)
        }
      },
      { signal },
    )
  }

  // Show initial tab
  if (config.updateHash && window.location.hash) {
    // Use URL hash if available
    showTabStep(window.location.hash, true, config)
  } else if (config.defaultHash) {
    // Otherwise use default
    showTabStep(config.defaultHash, true, config)
  } else if (tabs.length > 0) {
    if (!tabs[0].hash) {
      console.warn(
        '[initTabSteps] First tab has no hash fragment, cannot show default tab',
      )
    } else {
      showTabStep(tabs[0].hash, true, config)
    }
  }

  // Listen for hash changes (only if updateHash is enabled)
  if (config.updateHash) {
    window.addEventListener(
      'hashchange',
      function () {
        const currentTab = container.querySelector<HTMLAnchorElement>(
          '.nav .nav-link.active',
        )
        if (currentTab && currentTab.hash !== window.location.hash) {
          showTabStep(window.location.hash, true, config)
        }
      },
      { signal },
    )
  }

  // Validate all tabs on form submit
  const submitButton = container.querySelector<HTMLButtonElement>(
    'button[type=submit]',
  )
  if (submitButton) {
    submitButton.addEventListener(
      'click',
      function (event) {
        // Validate each tab in order
        for (const tab of tabs) {
          if (
            validateTabStepChange(tab, container as HTMLElement | Document)
              .length > 0
          ) {
            // Prevent submission and show first invalid tab
            event.preventDefault()
            showTabStep(tab.hash, true, config)
            break
          }
        }
      },
      { signal },
    )
  }

  return () => {
    controller.abort()
    initializedContainers.delete(container)
  }
}
